import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, BookOpen, Landmark, HandHeart } from "lucide-react";
import Reveal from "../components/Reveal";
import { images } from "../data/content";
import { useSiteContent } from "../lib/cms";

const threePillars = [
  { icon: BookOpen, title: "Philosophy", body: "The profound subjects of initiation, symbolism and tradition, and their potential to change a life for the better." },
  { icon: Landmark, title: "History", body: "How the traditions and teachings of the Craft came to be, and the role they have played in the moral search of mankind." },
  { icon: HandHeart, title: "Fellowship", body: "Brotherly love, charitable relief and truth — practised among men of every faith and background." },
];

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-b border-sapphire-deep/15" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 py-6 text-left"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-display text-xl font-semibold text-sapphire-deep md:text-2xl">{q}</span>
        <ChevronDown
          size={22}
          className={`shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] pb-6" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="font-sans text-[16px] leading-[1.75] text-charcoal/80">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function Freemasonry() {
  const faqs = useSiteContent().block("freemasonry_faqs").items || [];
  return (
    <div data-testid="freemasonry-page">
      <section className="relative flex min-h-[50vh] items-end">
        <img src={images.templeInterior} alt="Interior of a Masonic lodge room" className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="container-lodge relative z-10 pb-16 pt-28">
          <Reveal>
            <p className="eyebrow">The Craft</p>
            <h1 className="display-h1 mt-4 max-w-3xl text-parchment">What is Freemasonry?</h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper-texture bg-parchment py-24">
        <div className="container-lodge max-w-3xl">
          <Reveal>
            <div className="prose-lodge">
              <p className="font-display text-2xl leading-relaxed text-sapphire-deep">
                Freemasonry is a voluntary, fraternal organisation of men of good character who,
                in most jurisdictions, believe in an Almighty Creator and practise the spirit of
                universal brotherhood.
              </p>
              <p>
                For many men, Freemasonry fulfils a part of themselves they intrinsically felt was
                missing — whether social, philosophical, spiritual or historical. Freemasons help
                build a better world through a worthy process of building better men to live in it.
                The Freemason's motto is simple: <em>better men make a better world.</em>
              </p>
              <p>
                It can be defined as a "peculiar system of morality, veiled in allegory and
                illustrated by symbols." Within it, members advance through symbolic degrees in their
                intellectual and philosophical development. While Freemasonry is not a religion, it
                encourages every member to be active in his own faith.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="bg-sapphire-deep py-24 text-parchment">
        <div className="container-lodge">
          <Reveal>
            <p className="eyebrow">Three Pillars</p>
            <h2 className="display-h2 mt-3 text-parchment">Wisdom, Strength & Beauty.</h2>
            <div className="gold-rule mt-6" />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {threePillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.12}>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                  <p.icon size={30} strokeWidth={1.5} className="text-gold" />
                  <h3 className="mt-5 font-display text-2xl font-semibold text-parchment">{p.title}</h3>
                  <p className="mt-3 font-sans text-[15px] leading-relaxed text-parchment/75">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-parchment py-24">
        <div className="container-lodge max-w-3xl">
          <Reveal>
            <p className="eyebrow" style={{ color: "#046307" }}>Frequently Asked Questions</p>
            <h2 className="display-h2 mt-3 text-sapphire-deep">Honest answers.</h2>
            <div className="gold-rule mt-6" />
          </Reveal>
          <div className="mt-10">
            {faqs.map((f, i) => (
              <FaqItem key={`${f.q}-${i}`} q={f.q} a={f.a} index={i} />
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 text-center">
              <Link to="/visit" className="btn-dark" data-testid="freemasonry-inquiry">
                Curious? Begin an Inquiry <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
